/**
 * AccountMenu — identity, admin console entry point and logout.
 *
 * Phase 3: it belongs to the shell, so there is exactly one account control in
 * the authenticated user area (it used to live inside Header.jsx).
 *
 * Phase 4: the popover no longer claims `role="menu"` (it did not implement the
 * menu keyboard pattern). It is a disclosure: `aria-expanded` + `aria-controls`,
 * focus moved to the first item on open, Escape closes it and gives the focus
 * back to the trigger, Tab walks the items natively.
 */
import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, LogOut, Settings, ShieldCheck, UserRound } from "lucide-react";

function initials(name, email) {
  const source = (name || email || "?").trim();
  const parts = source.split(/[\s.@]+/).filter(Boolean);
  return (parts[0]?.[0] || "?").toUpperCase()
       + (parts[1]?.[0] || "").toUpperCase();
}

const ITEM_CLASS =
  "w-full flex items-center gap-2 min-h-10 px-2.5 py-2 rounded-lg text-sm text-content-secondary "
  + "hover:text-content hover:bg-surface-subtle transition-colors duration-fast ease-system "
  + "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";

export function AccountMenu({ user, isAdmin, onOpenProfile, onOpenAdmin, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = useId();

  const close = ({ restoreFocus = false } = {}) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector("button")?.focus();
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        ref={triggerRef}
        data-testid="account-menu-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label="Menu account"
        className="flex items-center gap-2 min-h-10 pl-1 pr-2 py-1 rounded-full border border-line
                   hover:bg-surface-subtle bg-surface/60 transition-colors duration-fast ease-system
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-8 h-8 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="w-8 h-8 rounded-full bg-brand/20 border border-brand/30 text-brand-accent text-label font-medium flex items-center justify-center">
            {initials(user?.name, user?.email)}
          </span>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-content-muted" aria-hidden="true" />
      </button>

      {open && (
        <div
          id={panelId}
          ref={panelRef}
          data-testid="account-menu"
          aria-label="Account"
          className="absolute right-0 mt-2 w-64 rounded-card border border-line bg-popover shadow-overlay p-1.5 z-50"
        >
          <div className="px-2.5 py-2">
            <p className="text-sm text-content truncate">
              {user?.name || user?.email}
            </p>
            <p className="text-xs text-content-muted truncate">{user?.email}</p>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-brand/15 border border-brand/30 text-brand-accent text-label">
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                Amministratore
              </span>
            )}
          </div>
          <div className="h-px bg-surface-subtle my-1" />
          <button
            data-testid="profile-menu-item"
            onClick={() => { close(); onOpenProfile(); }}
            className={ITEM_CLASS}
          >
            <UserRound className="w-4 h-4" aria-hidden="true" />
            Il mio profilo
          </button>
          {isAdmin && (
            <button
              data-testid="admin-menu-item"
              onClick={() => { close(); onOpenAdmin(); }}
              className={ITEM_CLASS}
            >
              <Settings className="w-4 h-4" aria-hidden="true" />
              Console di amministrazione
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className={`${ITEM_CLASS} hover:text-danger hover:bg-danger-solid/10`}
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Esci
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountMenu;
